/**
 * useStreamingUpload Hook
 *
 * Fault-tolerant, resumable multipart upload via the v2 streaming API.
 *
 * Key features:
 * - Auto-start session on first file selection
 * - Upload files via v2 multipart API (init → chunk → complete)
 * - Per-chunk automatic retry with exponential backoff
 * - Resume from last successful chunk on retryFile()
 * - AbortController per file for cancel support
 * - Auth token refresh before each chunk (safe for multi-GB uploads)
 * - Explicit "complete" action from user
 * - Domain-specific callback after completion
 *
 * Supports multiple file inputs (keyed) sharing one session.
 */

import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ToastComponent from '../../components/toast';
import { SessionManagementService } from '../../services/wealthlane-identity-services/services/SessionManagementService';
import { getStreamingHeaders } from '../../services/apiInterceptor';
import useWealthlaneIdentityApi from '../useWealthlaneIdentityApi';
import type {
  StreamingV2InitResponse,
  StreamingV2ChunkResponse,
  StreamingV2CompleteResponse,
  StreamingV2CompleteRequest,
  UploadContext,
} from '../../components/streaming-upload/types';
import { PERMANENT_ERROR_CODES } from '../../components/streaming-upload/types';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_MAX_CHUNK_RETRIES = 5;
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;

// ============================================================================
// Hook Types
// ============================================================================

export type FileStatus = 'empty' | 'queued' | 'uploading' | 'success' | 'failed';

export type UploadStatus = 'idle' | 'starting' | 'streaming' | 'completing' | 'completed' | 'error';

export interface FileState {
  file: File;
  status: FileStatus;
  progress: number;
  error?: string;
  /** Session file ID returned after successful upload (needed for deletion) */
  sessionFileId?: string;
}

export interface StreamingUploadConfig {
  /** Keys that must be uploaded before completion is allowed */
  requiredKeys?: string[];
  /** Max retries per chunk before the file upload is considered failed (default: 3) */
  maxChunkRetries?: number;
  /** Called after session is completed successfully */
  onAfterComplete?: (args: {
    sessionId: string;
    filesByKey: Record<string, File>;
  }) => Promise<void> | void;
  /** Called on error */
  onError?: (error: Error, context: { key?: string; phase: string }) => void;
}

/** Separator used to create group sub-keys for multi-file uploads */
export const GROUP_SEPARATOR = ':::';

export interface UseStreamingUploadReturn {
  // State
  status: UploadStatus;
  sessionId: string | undefined;
  filesByKey: Record<string, FileState | undefined>;
  /** Validation errors by key (for required file fields) */
  validationErrors: Record<string, string | undefined>;

  // Actions
  setFile: (key: string, file: File | undefined) => Promise<void>;
  removeFile: (key: string) => Promise<void>;
  retryFile: (key: string) => Promise<void>;
  complete: () => Promise<void>;
  reset: () => void;
  /** Validate required fields and return true if valid */
  validate: () => boolean;
  /** Clear validation error for a specific key */
  clearValidationError: (key: string) => void;

  // Multi-file group actions
  /** Add multiple files to a group (each gets a unique sub-key under groupKey) */
  addFilesToGroup: (groupKey: string, files: File[]) => Promise<void>;
  /** Get all file states belonging to a group */
  getFileGroup: (groupKey: string) => Array<{ key: string; state: FileState }>;

  // Computed
  canComplete: boolean;
  isUploading: boolean;
  isCompleting: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

function isPermanentError(status: number): boolean {
  return PERMANENT_ERROR_CODES.has(status);
}

// ============================================================================
// Hook
// ============================================================================

export function useStreamingUpload(config: StreamingUploadConfig = {}): UseStreamingUploadReturn {
  const {
    requiredKeys = [],
    maxChunkRetries = DEFAULT_MAX_CHUNK_RETRIES,
    onAfterComplete,
    onError,
  } = config;

  const { handleRequest } = useWealthlaneIdentityApi();

  const [status, setStatus] = useState<UploadStatus>('idle');
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [filesByKey, setFilesByKey] = useState<Record<string, FileState | undefined>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  const sessionStartingRef = useRef(false);
  const sessionIdRef = useRef<string | undefined>(undefined);
  const sessionPromiseRef = useRef<Promise<string> | null>(null);

  /** Per-key lock to prevent concurrent uploads for the same key. */
  const uploadLocksRef = useRef<Set<string>>(new Set());

  /** AbortController per active file upload. */
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  /**
   * Intermediate multipart state per key.
   * Preserved across failures so retryFile() can resume.
   */
  const uploadContextsRef = useRef<Map<string, UploadContext>>(new Map());

  // --------------------------------------------------------------------------
  // ensureSession
  // --------------------------------------------------------------------------

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionIdRef.current) {
      return sessionIdRef.current;
    }

    if (sessionPromiseRef.current) {
      return sessionPromiseRef.current;
    }

    const promise = (async () => {
      sessionStartingRef.current = true;
      setStatus('starting');

      try {
        const response = await handleRequest(
          SessionManagementService.postApiIdentityApiSessionsStart()
        );
        const newSessionId = (response as any)?.id ?? (response as any)?.sessionId;
        if (!newSessionId) {
          throw new Error('Failed to get sessionId from start session response');
        }
        sessionIdRef.current = newSessionId;
        setSessionId(newSessionId);
        setStatus('idle');
        return newSessionId;
      } catch (error) {
        setStatus('error');
        sessionPromiseRef.current = null;
        const err = error instanceof Error ? error : new Error('Failed to start session');
        onError?.(err, { phase: 'start' });
        throw err;
      } finally {
        sessionStartingRef.current = false;
      }
    })();

    sessionPromiseRef.current = promise;
    return promise;
  }, [handleRequest, onError]);

  // --------------------------------------------------------------------------
  // uploadSingleChunk (with retry)
  // --------------------------------------------------------------------------

  /**
   * Upload a single chunk with automatic retry and exponential backoff.
   * Retries on transient errors; throws immediately on permanent errors
   * (4xx client errors, 500/501) or if max retries are exhausted.
   */
  const uploadSingleChunk = useCallback(
    async (
      baseURL: string,
      params: { sessionId: string; uploadId: string; key: string; partNumber: number },
      chunkBlob: Blob,
      signal: AbortSignal
    ): Promise<StreamingV2ChunkResponse> => {
      let lastError: any;

      for (let attempt = 0; attempt <= maxChunkRetries; attempt++) {
        if (signal.aborted) {
          throw new DOMException('Upload cancelled', 'AbortError');
        }

        try {
          const headers = await getStreamingHeaders();
          const queryParams = new URLSearchParams({
            SessionId: params.sessionId,
            UploadId: params.uploadId,
            Key: params.key,
            PartNumber: String(params.partNumber),
          });

          const response = await fetch(
            `${baseURL}/api/identity/api/streaming/v2/chunk?${queryParams.toString()}`,
            {
              method: 'POST',
              body: chunkBlob,
              headers: { ...headers, 'Content-Type': 'application/octet-stream' },
              signal,
            }
          );

          if (response.ok) {
            return await response.json();
          }

          const body = await response.text().catch(() => '');
          const err = Object.assign(
            new Error(`Chunk ${params.partNumber} failed – HTTP ${response.status}: ${body}`),
            { status: response.status }
          );

          if (isPermanentError(response.status)) {
            throw err;
          }

          lastError = err;
        } catch (error: any) {
          if (error?.name === 'AbortError') throw error;
          if (error?.status && isPermanentError(error.status)) throw error;
          lastError = error;
        }

        // Transient error — wait with exponential backoff before retrying
        if (attempt < maxChunkRetries) {
          const backoffMs = Math.min(
            INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt),
            MAX_RETRY_DELAY_MS
          );
          await delay(backoffMs, signal);
        }
      }

      throw (
        lastError ?? new Error(`Chunk ${params.partNumber} failed after ${maxChunkRetries} retries`)
      );
    },
    [maxChunkRetries]
  );

  // --------------------------------------------------------------------------
  // streamFileToServer
  // --------------------------------------------------------------------------

  const streamFileToServer = useCallback(
    async (key: string, file: File, currentSessionId: string): Promise<void> => {
      if (uploadLocksRef.current.has(key)) {
        return;
      }
      uploadLocksRef.current.add(key);

      const abortController = new AbortController();
      abortControllersRef.current.set(key, abortController);
      const { signal } = abortController;

      // Check for existing context (resume path)
      const existingCtx = uploadContextsRef.current.get(key);
      const isResume = existingCtx != null && existingCtx.totalBytes === file.size;

      const resumeProgress = isResume
        ? Math.round((existingCtx.completedParts.length / existingCtx.totalChunks) * 100)
        : 0;

      setFilesByKey((prev) => ({
        ...prev,
        [key]: { file, status: 'uploading', progress: resumeProgress },
      }));
      setStatus('streaming');

      try {
        const baseURL = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
        let ctx: UploadContext;

        if (isResume) {
          // Resume: reuse existing init data, skip completed chunks
          ctx = existingCtx;
        } else {
          // Fresh upload: call init
          const initHeaders = await getStreamingHeaders();
          const initParams = new URLSearchParams({
            SessionId: currentSessionId,
            FileName: file.name,
          });
          const initResponse = await fetch(
            `${baseURL}/api/identity/api/streaming/v2/init?${initParams.toString()}`,
            { method: 'POST', headers: initHeaders, signal }
          );

          if (!initResponse.ok) {
            const body = await initResponse.text().catch(() => '');
            throw Object.assign(new Error(`Init failed – HTTP ${initResponse.status}: ${body}`), {
              status: initResponse.status,
            });
          }

          const initData: StreamingV2InitResponse = await initResponse.json();
          const chunkSize = initData.minChunkSizeInBytes || initData.recommendedChunkSizeInBytes;
          const totalBytes = file.size;

          ctx = {
            sessionId: currentSessionId,
            uploadId: initData.uploadId,
            objectKey: initData.key,
            chunkSize,
            totalChunks: Math.ceil(totalBytes / chunkSize),
            totalBytes,
            completedParts: [],
          };
          uploadContextsRef.current.set(key, ctx);
        }

        // Build a set of already-completed part numbers for O(1) lookup
        const completedSet = new Set(ctx.completedParts.map((p) => p.partNumber));

        // ── Upload chunks (skipping already completed) ──
        for (let i = 0; i < ctx.totalChunks; i++) {
          const partNumber = i + 1;
          if (completedSet.has(partNumber)) {
            continue;
          }

          if (signal.aborted) {
            throw new DOMException('Upload cancelled', 'AbortError');
          }

          const start = i * ctx.chunkSize;
          const end = Math.min(start + ctx.chunkSize, ctx.totalBytes);
          const chunkBlob = file.slice(start, end);

          const chunkData = await uploadSingleChunk(
            baseURL,
            {
              sessionId: ctx.sessionId,
              uploadId: ctx.uploadId,
              key: ctx.objectKey,
              partNumber,
            },
            chunkBlob,
            signal
          );

          ctx.completedParts.push({ partNumber: chunkData.partNumber, eTag: chunkData.eTag });

          const percent = Math.round((ctx.completedParts.length / ctx.totalChunks) * 100);
          setFilesByKey((prev) => ({
            ...prev,
            [key]: { ...prev[key]!, progress: Math.min(percent, 99) },
          }));
        }

        // ── Complete ──
        const completeHeaders = await getStreamingHeaders();
        const completeBody: StreamingV2CompleteRequest = {
          sessionId: ctx.sessionId,
          uploadId: ctx.uploadId,
          key: ctx.objectKey,
          fileName: file.name,
          parts: ctx.completedParts,
        };
        const completeResponse = await fetch(`${baseURL}/api/identity/api/streaming/v2/complete`, {
          method: 'POST',
          body: JSON.stringify(completeBody),
          headers: { ...completeHeaders, 'Content-Type': 'application/json' },
          signal,
        });

        if (!completeResponse.ok) {
          const body = await completeResponse.text().catch(() => '');
          throw Object.assign(
            new Error(`Complete failed – HTTP ${completeResponse.status}: ${body}`),
            { status: completeResponse.status }
          );
        }

        const completeData: StreamingV2CompleteResponse = await completeResponse.json();

        // Success — clear saved context
        uploadContextsRef.current.delete(key);

        setFilesByKey((prev) => ({
          ...prev,
          [key]: {
            file,
            status: 'success',
            progress: 100,
            sessionFileId: completeData.sessionFileId,
          },
        }));
        setStatus((cur) => (cur === 'streaming' ? 'idle' : cur));
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return;
        }

        console.error('[StreamingUpload] Upload failed:', error);

        const errorStatus = error?.status;
        const errorString = error?.message || String(error);
        const is413Error =
          errorStatus === 413 ||
          errorString.includes('413') ||
          errorString.toLowerCase().includes('too large') ||
          errorString.toLowerCase().includes('content too large');

        const ctx = uploadContextsRef.current.get(key);
        const failedProgress = ctx
          ? Math.round((ctx.completedParts.length / ctx.totalChunks) * 100)
          : 0;

        const errorMessage = is413Error
          ? 'File size is too large. Please select a smaller file.'
          : 'Failed to upload file';

        const err = new Error(errorMessage);
        setFilesByKey((prev) => ({
          ...prev,
          [key]: { file, status: 'failed', progress: failedProgress, error: errorMessage },
        }));
        setStatus('error');
        onError?.(err, { key, phase: 'stream' });
        throw err;
      } finally {
        uploadLocksRef.current.delete(key);
        abortControllersRef.current.delete(key);
      }
    },
    [onError, uploadSingleChunk]
  );

  // --------------------------------------------------------------------------
  // removeFile
  // --------------------------------------------------------------------------

  const removeFile = useCallback(
    async (key: string): Promise<void> => {
      abortControllersRef.current.get(key)?.abort();
      abortControllersRef.current.delete(key);
      uploadLocksRef.current.delete(key);
      uploadContextsRef.current.delete(key);

      const fileState = filesByKey[key];

      if (fileState?.sessionFileId && fileState.status === 'success') {
        try {
          await handleRequest(
            SessionManagementService.deleteApiIdentityApiSessionsFiles(fileState.sessionFileId)
          );
        } catch (error) {
          console.error('Failed to delete file from server:', error);
        }
      }

      setFilesByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      setValidationErrors((prev) => {
        const groupKey = key.includes(GROUP_SEPARATOR) ? key.split(GROUP_SEPARATOR)[0] : undefined;
        const hasKey = prev[key] !== undefined;
        const hasGroupKey = groupKey !== undefined && prev[groupKey] !== undefined;
        if (!hasKey && !hasGroupKey) return prev;
        const next = { ...prev };
        if (hasKey) delete next[key];
        if (hasGroupKey) delete next[groupKey];
        return next;
      });
    },
    [filesByKey, handleRequest]
  );

  // --------------------------------------------------------------------------
  // setFile
  // --------------------------------------------------------------------------

  const setFile = useCallback(
    async (key: string, file: File | undefined): Promise<void> => {
      setValidationErrors((prev) => {
        const groupKey = key.includes(GROUP_SEPARATOR) ? key.split(GROUP_SEPARATOR)[0] : undefined;
        const hasKey = prev[key] !== undefined;
        const hasGroupKey = groupKey !== undefined && prev[groupKey] !== undefined;
        if (!hasKey && !hasGroupKey) return prev;
        const next = { ...prev };
        if (hasKey) delete next[key];
        if (hasGroupKey) delete next[groupKey];
        return next;
      });

      if (!file) {
        await removeFile(key);
        return;
      }

      // Abort any in-progress upload and clear its saved context (new file = fresh start)
      if (uploadLocksRef.current.has(key)) {
        abortControllersRef.current.get(key)?.abort();
        abortControllersRef.current.delete(key);
        uploadLocksRef.current.delete(key);
      }
      uploadContextsRef.current.delete(key);

      setFilesByKey((prev) => ({
        ...prev,
        [key]: { file, status: 'queued', progress: 0 },
      }));

      try {
        const currentSessionId = await ensureSession();
        await streamFileToServer(key, file, currentSessionId);
      } catch {
        // Error already handled in ensureSession or streamFileToServer
      }
    },
    [ensureSession, streamFileToServer, removeFile]
  );

  // --------------------------------------------------------------------------
  // retryFile (resumes from last successful chunk)
  // --------------------------------------------------------------------------

  const retryFile = useCallback(
    async (key: string): Promise<void> => {
      const fileState = filesByKey[key];
      if (!fileState || fileState.status !== 'failed') {
        return;
      }

      try {
        const currentSessionId = await ensureSession();
        await streamFileToServer(key, fileState.file, currentSessionId);
      } catch {
        // Error already handled
      }
    },
    [filesByKey, ensureSession, streamFileToServer]
  );

  // --------------------------------------------------------------------------
  // addFilesToGroup
  // --------------------------------------------------------------------------

  const addFilesToGroup = useCallback(
    async (groupKey: string, files: File[]): Promise<void> => {
      const timestamp = Date.now();

      const fileKeys = files.map((file, index) => {
        const fileKey = `${groupKey}${GROUP_SEPARATOR}${timestamp}_${index}`;
        setFilesByKey((prev) => ({
          ...prev,
          [fileKey]: { file, status: 'queued', progress: 0 },
        }));
        return fileKey;
      });

      for (let i = 0; i < files.length; i++) {
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        await setFile(fileKeys[i], files[i]).catch(() => {
          // Error already handled inside setFile; continue with next file
        });
      }
    },
    [setFile]
  );

  // --------------------------------------------------------------------------
  // getFileGroup
  // --------------------------------------------------------------------------

  const getFileGroup = useCallback(
    (groupKey: string): Array<{ key: string; state: FileState }> => {
      const prefix = `${groupKey}${GROUP_SEPARATOR}`;
      return Object.entries(filesByKey)
        .filter(([k, state]) => k.startsWith(prefix) && state !== undefined)
        .map(([k, state]) => ({ key: k, state: state! }));
    },
    [filesByKey]
  );

  // --------------------------------------------------------------------------
  // validate
  // --------------------------------------------------------------------------

  const validate = useCallback((): boolean => {
    const errors: Record<string, string | undefined> = {};
    let isValid = true;

    for (const key of requiredKeys) {
      const fileState = filesByKey[key];
      const prefix = `${key}${GROUP_SEPARATOR}`;
      const groupEntries = Object.entries(filesByKey).filter(
        ([k, s]) => k.startsWith(prefix) && s !== undefined
      );

      if (groupEntries.length > 0) {
        const hasFailedFiles = groupEntries.some(([, s]) => s!.status === 'failed');
        const hasPendingFiles = groupEntries.some(
          ([, s]) => s!.status !== 'success' && s!.status !== 'failed'
        );
        if (hasFailedFiles) {
          errors[key] = 'Some files failed to upload. Please retry or remove them.';
          isValid = false;
        } else if (hasPendingFiles) {
          errors[key] = 'Please wait for all uploads to complete';
          isValid = false;
        }
      } else if (!fileState) {
        errors[key] = 'Required field';
        isValid = false;
      } else if (fileState.status === 'failed') {
        errors[key] = fileState.error || 'Upload failed. Please retry.';
        isValid = false;
      } else if (fileState.status !== 'success') {
        errors[key] = 'Please wait for upload to complete';
        isValid = false;
      }
    }

    setValidationErrors(errors);
    return isValid;
  }, [filesByKey, requiredKeys]);

  // --------------------------------------------------------------------------
  // clearValidationError
  // --------------------------------------------------------------------------

  const clearValidationError = useCallback((key: string): void => {
    setValidationErrors((prev) => {
      if (prev[key] === undefined) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // --------------------------------------------------------------------------
  // complete (session-level)
  // --------------------------------------------------------------------------

  const complete = useCallback(async (): Promise<void> => {
    if (!validate()) {
      return;
    }

    if (!sessionIdRef.current) {
      toast.error(<ToastComponent label='Error' message='No upload session to complete' />, {
        autoClose: false,
      });
      return;
    }

    setStatus('completing');

    try {
      await handleRequest(
        SessionManagementService.postApiIdentityApiSessionsComplete(sessionIdRef.current)
      );

      const files: Record<string, File> = {};
      Object.entries(filesByKey).forEach(([key, state]) => {
        if (state?.file) {
          files[key] = state.file;
        }
      });

      if (onAfterComplete) {
        await onAfterComplete({ sessionId: sessionIdRef.current, filesByKey: files });
      }

      setStatus('completed');
    } catch (error) {
      setStatus('error');
      const err = error instanceof Error ? error : new Error('Failed to complete session');
      onError?.(err, { phase: 'complete' });
    }
  }, [validate, handleRequest, filesByKey, onAfterComplete, onError]);

  // --------------------------------------------------------------------------
  // reset
  // --------------------------------------------------------------------------

  const reset = useCallback((): void => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
    uploadLocksRef.current.clear();
    uploadContextsRef.current.clear();

    setStatus('idle');
    setSessionId(undefined);
    setFilesByKey({});
    setValidationErrors({});
    sessionIdRef.current = undefined;
    sessionStartingRef.current = false;
    sessionPromiseRef.current = null;
  }, []);

  // --------------------------------------------------------------------------
  // Computed
  // --------------------------------------------------------------------------

  const canComplete =
    status !== 'starting' &&
    status !== 'streaming' &&
    status !== 'completing' &&
    status !== 'completed' &&
    sessionIdRef.current !== undefined &&
    requiredKeys.every((key) => {
      const prefix = `${key}${GROUP_SEPARATOR}`;
      const groupEntries = Object.entries(filesByKey).filter(
        ([k, s]) => k.startsWith(prefix) && s !== undefined
      );
      if (groupEntries.length > 0) {
        return groupEntries.every(([, s]) => s!.status === 'success');
      }
      return filesByKey[key]?.status === 'success';
    });

  const isUploading = status === 'starting' || status === 'streaming';
  const isCompleting = status === 'completing';

  return {
    status,
    sessionId,
    filesByKey,
    validationErrors,
    setFile,
    removeFile,
    retryFile,
    complete,
    reset,
    validate,
    clearValidationError,
    addFilesToGroup,
    getFileGroup,
    canComplete,
    isUploading,
    isCompleting,
  };
}

export default useStreamingUpload;
